// Ported from deexoptions-mobile-app/hooks/queries/useRecentTransactions.ts
// (see useTransactions.ts for the useAuthAxios adaptation note).

import { useQuery } from "@tanstack/react-query";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";
import {
  normalizeRecentTransaction,
  type ActivityItem,
} from "../../utils/activity/normalizeTransaction";
import { transactionKeys } from "./transactionKeys";
import { ACTIVITY_STALE_TIME } from "./activityKeys";

/**
 * The "recent transactions" feed — one request, already merged and sorted
 * server-side. This is the only mixed-type feed the API offers; every other
 * list requires an explicit `type`.
 */

interface RecentTransactionsResponse {
  data?: { transactions?: any[] };
}

const buildRecentQuery = (limit: number, coin?: string) => {
  const params = new URLSearchParams({ limit: String(limit) });
  // Crypto-only scoping for the wallet screen; the dashboard sends no coin and
  // gets every type back.
  if (coin) params.set("coin", coin.toLowerCase());
  return params.toString();
};

type Options = {
  coin?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
};

export const useRecentTransactions = (limit: number, options?: Options) => {
  const axiosInstance = useAuthAxios();
  const coin = options?.coin || undefined;

  const query = useQuery({
    queryKey: transactionKeys.recent(coin, limit),
    queryFn: async (): Promise<RecentTransactionsResponse> => {
      const { data } = await axiosInstance.get(
        API.getRecentTransactions(buildRecentQuery(limit, coin))
      );
      return data;
    },
    enabled: options?.enabled ?? true,
    staleTime: ACTIVITY_STALE_TIME,
    refetchInterval: options?.refetchInterval,
    // Normalize once per fetch, not per render.
    select: (data): ActivityItem[] =>
      (data?.data?.transactions ?? []).map(normalizeRecentTransaction),
  });

  return {
    items: query.data ?? [],
    isPending: query.isPending,
    // Distinct from isPending so a manual refresh of already-loaded data can
    // still show the spinner.
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
};
